/**
 * useStableControlBarState - Stable state management for AgentControlBar
 *
 * Prevents flickering by:
 * - Maintaining persistent UI state independent of raw socket events
 * - Debouncing rapid state changes (800ms protection)
 * - Showing bar immediately on mount
 * - Keeping bar visible until explicitly ended
 * - Only updating visual indicators, never unmounting
 */
import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * State machine for control bar visibility
 */
const BAR_STATE = {
  VISIBLE: 'visible', // Bar is visible and active
  ENDED: 'ended', // Call has ended, bar should hide
};

/**
 * Connection status states (for visual indicators only)
 */
const CONNECTION_STATUS = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected',
};

/**
 * Hook for stable control bar state management
 *
 * @param {Object} options
 * @param {boolean} options.isConnected - Raw connection state from LiveKit
 * @param {boolean} options.isReconnecting - Whether reconnection is in progress
 * @param {boolean} options.isAISpeaking - Whether AI is currently speaking
 * @param {boolean} options.isListening - Whether listening for user input
 * @param {boolean} options.isCallEnded - Whether the call has explicitly ended
 * @returns {Object} Stable state object with connectionStatus, isVisible, etc.
 */
export function useStableControlBarState({
  isConnected = false,
  isReconnecting = false,
  isAISpeaking = false,
  isListening = false,
  isCallEnded = false,
}) {
  // Persistent visibility state - once visible, stays visible until call ends
  const [barState, setBarState] = useState(BAR_STATE.VISIBLE);

  // Stable connection status (debounced)
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.CONNECTING);

  // Stable speaking/listening states (debounced)
  const [stableIsAISpeaking, setStableIsAISpeaking] = useState(false);
  const [stableIsListening, setStableIsListening] = useState(false);

  // Refs for debouncing - separate timestamps for each state to prevent conflicts
  const debounceTimeoutRef = useRef(null);
  const connectionLastChangeRef = useRef(Date.now());
  const speakingLastChangeRef = useRef(Date.now());
  const listeningLastChangeRef = useRef(Date.now());
  const rapidChangeGuardRef = useRef(null);
  const speakingTimeoutRef = useRef(null);
  const listeningTimeoutRef = useRef(null);

  // Track if bar has been shown (once shown, always visible)
  const hasBeenVisibleRef = useRef(false);

  // Show bar immediately on mount
  useEffect(() => {
    if (!hasBeenVisibleRef.current) {
      hasBeenVisibleRef.current = true;
      setBarState(BAR_STATE.VISIBLE);
    }
  }, []);

  // Handle call end - hide bar
  useEffect(() => {
    if (isCallEnded) {
      setBarState(BAR_STATE.ENDED);
    }
  }, [isCallEnded]);

  // Debounced connection status update with 800ms protection
  useEffect(() => {
    // Clear any pending updates
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    // Clear rapid change guard
    if (rapidChangeGuardRef.current) {
      clearTimeout(rapidChangeGuardRef.current);
      rapidChangeGuardRef.current = null;
    }

    const now = Date.now();
    const timeSinceLastChange = now - connectionLastChangeRef.current;

    // If less than 800ms since last change, delay the update
    const delay = timeSinceLastChange < 800 ? 800 - timeSinceLastChange : 0;

    debounceTimeoutRef.current = setTimeout(() => {
      // Determine connection status
      let newStatus;
      if (isCallEnded) {
        newStatus = CONNECTION_STATUS.DISCONNECTED;
      } else if (isReconnecting) {
        newStatus = CONNECTION_STATUS.RECONNECTING;
      } else if (isConnected) {
        newStatus = CONNECTION_STATUS.CONNECTED;
      } else {
        newStatus = CONNECTION_STATUS.CONNECTING;
      }

      // Only update if status actually changed
      setConnectionStatus((prevStatus) => {
        if (prevStatus !== newStatus) {
          connectionLastChangeRef.current = Date.now();
          return newStatus;
        }
        return prevStatus;
      });

      debounceTimeoutRef.current = null;
    }, delay);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      if (rapidChangeGuardRef.current) {
        clearTimeout(rapidChangeGuardRef.current);
        rapidChangeGuardRef.current = null;
      }
    };
  }, [isConnected, isReconnecting, isCallEnded]);

  // Debounced speaking state update
  useEffect(() => {
    if (speakingTimeoutRef.current) {
      clearTimeout(speakingTimeoutRef.current);
      speakingTimeoutRef.current = null;
    }

    const now = Date.now();
    const timeSinceLastChange = now - speakingLastChangeRef.current;
    const delay = timeSinceLastChange < 800 ? 800 - timeSinceLastChange : 0;

    speakingTimeoutRef.current = setTimeout(() => {
      setStableIsAISpeaking((prev) => {
        if (prev !== isAISpeaking) {
          speakingLastChangeRef.current = Date.now();
          return isAISpeaking;
        }
        return prev;
      });
      speakingTimeoutRef.current = null;
    }, delay);

    return () => {
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
        speakingTimeoutRef.current = null;
      }
    };
  }, [isAISpeaking]);

  // Debounced listening state update
  useEffect(() => {
    if (listeningTimeoutRef.current) {
      clearTimeout(listeningTimeoutRef.current);
      listeningTimeoutRef.current = null;
    }

    const now = Date.now();
    const timeSinceLastChange = now - listeningLastChangeRef.current;
    const delay = timeSinceLastChange < 800 ? 800 - timeSinceLastChange : 0;

    listeningTimeoutRef.current = setTimeout(() => {
      setStableIsListening((prev) => {
        if (prev !== isListening) {
          listeningLastChangeRef.current = Date.now();
          return isListening;
        }
        return prev;
      });
      listeningTimeoutRef.current = null;
    }, delay);

    return () => {
      if (listeningTimeoutRef.current) {
        clearTimeout(listeningTimeoutRef.current);
        listeningTimeoutRef.current = null;
      }
    };
  }, [isListening]);

  // Computed stable isConnected for AgentControlBar
  const stableIsConnected = connectionStatus === CONNECTION_STATUS.CONNECTED;

  // Bar visibility - always visible unless call ended
  const isVisible = barState === BAR_STATE.VISIBLE;

  return {
    // Visibility state
    isVisible,

    // Stable connection state (for AgentControlBar isConnected prop)
    isConnected: stableIsConnected,

    // Connection status enum (for internal logic)
    connectionStatus,

    // Stable speaking/listening states
    isAISpeaking: stableIsAISpeaking,
    isListening: stableIsListening,

    // Raw states (for debugging)
    raw: {
      isConnected,
      isReconnecting,
      isAISpeaking,
      isListening,
      isCallEnded,
    },
  };
}
